import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { HYDRATE } from "@/redux/reducers/hydration";

export type SearchSuggestion = {
  distanceMi?: number;
  iconFilePath: string;
  id?: number;
  link: string;
  title: string;
  subtitle?: string;
};

export type HeaderState = {
  collapsed: boolean;
  loadingSpinner: {
    reasons: Array<string>;
  };
  navigationOpen: boolean;
  notificationCount?: number;
  reloadPageAfterLocationChange: boolean;
  search: {
    open: boolean;
    recentSearches: Array<SearchSuggestion>;
    suggestions: Array<SearchSuggestion>;
  };
  showBanner: boolean;
};

export const initialState: HeaderState = {
  collapsed: false,
  loadingSpinner: {
    reasons: [],
  },
  navigationOpen: false,
  reloadPageAfterLocationChange: true,
  search: {
    open: false,
    recentSearches: [],
    suggestions: [],
  },
  showBanner: false,
};

const getRecentSearchHistory = (storage?: Storage): Array<SearchSuggestion> => {
  const recentSearches = storage?.getItem("recentSearches")?.split(",");

  if (recentSearches) {
    return recentSearches.map((rs) => ({
      iconFilePath: "refresh.svg",
      link: "/search?q=" + encodeURIComponent(rs),
      title: rs,
    }));
  }

  return [];
};

const MAX_RECENT_SEARCH_ITEMS = 7;
const purgeBadCharacters = (s?: string) =>
  s?.replace(/[^a-zA-Z0-9\s\-.'&]/g, "");
const updateRecentSearchHistory = (searchText: string, storage?: Storage) => {
  const recentSearches =
    storage
      ?.getItem("recentSearches")
      ?.split(",")
      .slice(0, MAX_RECENT_SEARCH_ITEMS) || [];

  const sanitizedSearchText = purgeBadCharacters(searchText);
  if (
    sanitizedSearchText &&
    recentSearches.indexOf(sanitizedSearchText) === -1
  ) {
    storage?.setItem(
      "recentSearches",
      [sanitizedSearchText, ...recentSearches].join(","),
    );
  }
};

const headerSlice = createSlice({
  extraReducers: (builder) => {
    /**
     * Ignore any hydration events after initial page load to preserve any
     * subsequent changes that have been made on the client.
     */
    builder.addCase(HYDRATE, () => {});
  },
  initialState,
  name: "header",
  reducers: {
    closeNavigation: (state) => {
      state.navigationOpen = false;
    },
    closeSearch: (state) => {
      state.search.open = false;
    },
    disableLoadingSpinner: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      if (action.payload) {
        state.loadingSpinner.reasons = state.loadingSpinner.reasons.filter(
          (r) => r !== action.payload,
        );
      } else {
        state.loadingSpinner.reasons = [];
      }
    },
    disableLocationChangeReload: (state) => {
      state.reloadPageAfterLocationChange = false;
    },
    enableLoadingSpinner: (state, action: PayloadAction<string>) => {
      state.loadingSpinner.reasons.push(action.payload);
    },
    enableLocationChangeReload: (state) => {
      state.reloadPageAfterLocationChange = true;
    },
    hideBanner: (state) => {
      state.showBanner = false;
    },
    openNavigation: (state) => {
      state.navigationOpen = true;
    },
    openSearch: (
      state,
      action: PayloadAction<{
        searchText: string | null;
        storage?: Storage;
      }>,
    ) => {
      state.search.open = true;
      state.search.recentSearches = action.payload.storage
        ? getRecentSearchHistory(action.payload.storage)
        : [];
      state.search.suggestions =
        action.payload.searchText && action.payload.searchText.length > 2
          ? state.search.suggestions
          : [];
    },
    showBanner: (state) => {
      state.showBanner = true;
    },
    submitSearch: (
      state,
      action: PayloadAction<{
        searchText: string | null;
        storage?: Storage;
      }>,
    ) => {
      if (action.payload?.searchText) {
        updateRecentSearchHistory(
          action.payload.searchText,
          action.payload?.storage,
        );
      }
      // Reset to initial state
      Object.assign(state, initialState);
    },
    updateNotificationCount: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    updateSearchSuggestions: (
      state,
      action: PayloadAction<Array<SearchSuggestion>>,
    ) => {
      state.search.suggestions = action.payload || [];
    },
  },
});

export const {
  closeNavigation,
  closeSearch,
  disableLoadingSpinner,
  disableLocationChangeReload,
  enableLoadingSpinner,
  enableLocationChangeReload,
  hideBanner,
  openNavigation,
  openSearch,
  showBanner,
  submitSearch,
  updateNotificationCount,
  updateSearchSuggestions,
} = headerSlice.actions;

export default headerSlice.reducer;
